import React, { useState } from "react";
import { Card, CardBody, CardHeader, Collapse, Progress, Tooltip as ReactstrapTooltip, Badge } from "reactstrap";
import { tipoEfectivo } from "../../utils/validaciones";

// Custom Tooltip wrapper to avoid defaultProps warning
const Tooltip = ({ children, ...props }) => {
  // Set default props using JavaScript default parameters
  const finalProps = {
    placement: 'top',
    ...props
  };
  
  return <ReactstrapTooltip {...finalProps}>{children}</ReactstrapTooltip>;
};

/**
 * Componente de Progreso de Solicitud - Stepper Vertical
 * Muestra el avance de una solicitud a través de sus diferentes etapas
 * Soporta tanto solicitudes Agudas como Crónicas
 */
const ProgresoSolicitud = ({ solicitud }) => {
  const [isOpen, setIsOpen] = useState(true);

  if (!solicitud) return null;

  const tipoOp = tipoEfectivo(solicitud);
  const esCronica = tipoOp === "Cronica";
  
  const etapas = esCronica ? calcularEtapasCronicas(solicitud) : calcularEtapasAgudas(solicitud);
  const porcentaje = solicitud.porcentaje_avance || calcularPorcentaje(etapas);

  const infoCiclo = esCronica ? obtenerInfoCiclo(solicitud) : null;

  // Inicializar estado de tooltips con todas las etapas posibles
  const initialTooltipState = {};
  etapas.forEach(etapa => {
    initialTooltipState[`etapa-${etapa.id}-${solicitud.identificador}`] = false;
  });
  const [tooltipOpen, setTooltipOpen] = useState(initialTooltipState);

  // Función para manejar tooltips
  const toggleTooltip = (id) => {
    setTooltipOpen(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Determinar color de la barra de progreso
  const getProgressColor = () => {
    if (porcentaje >= 100) return "success";
    if (porcentaje >= 60) return "primary";
    if (porcentaje >= 40) return "info";
    return "warning";
  };

  return (
    <Card className="mb-3">
      <CardHeader 
        className="d-flex align-items-center justify-content-between cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex align-items-center flex-wrap gap-2">
          <i className="ri-route-line fs-18 me-2 text-primary"></i>
          <h6 className="mb-0">
            {esCronica ? "Progreso del Ciclo" : "Progreso de Avance"}
          </h6>
          {esCronica && infoCiclo && (
            <Badge color="info" className="fs-10">
              Ciclo {infoCiclo.cicloActual} de {infoCiclo.totalCiclos}
            </Badge>
          )}
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center">
            <span className="text-muted me-2 small">Avance:</span>
            <span className={`fw-semibold text-${getProgressColor()}`}>
              {porcentaje.toFixed(0)}%
            </span>
          </div>
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line fs-18 text-muted`}></i>
        </div>
      </CardHeader>
      
      <Collapse isOpen={isOpen}>
        <CardBody className="pt-3 pb-2">

          {/* Barra de progreso general */}
          <Progress 
            value={porcentaje} 
            color={getProgressColor()} 
            className="mb-4" 
            style={{ height: "8px" }}
          />

          {/* Stepper Vertical */}
          <div className="stepper-vertical">
            {etapas.map((etapa, index) => (
              <div 
                key={etapa.id} 
                className={`stepper-item ${etapa.estado}`}
                id={`etapa-${etapa.id}-${solicitud.identificador}`}
              >
                {/* Línea conectora */}
                {index < etapas.length - 1 && (
                  <div className={`stepper-line ${etapa.estado === 'completado' ? 'completado' : ''}`}></div>
                )}
                
                {/* Icono de estado */}
                <div className={`stepper-icon ${etapa.estado}`}>
                  {etapa.estado === 'completado' ? (
                    <i className="ri-check-line"></i>
                  ) : etapa.estado === 'actual' ? (
                    <i className={etapa.icono}></i>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>

                {/* Contenido */}
                <div className="stepper-content">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <h6 className={`mb-0 ${etapa.estado === 'pendiente' ? 'text-muted' : ''}`}>
                      {etapa.titulo}
                    </h6>
                    {etapa.estado === 'actual' && (
                      <span className="badge bg-primary-subtle text-primary fs-10">
                        En progreso
                      </span>
                    )}
                  </div>
                  <p className={`mb-0 small ${etapa.estado === 'pendiente' ? 'text-muted' : 'text-muted'}`}>
                    {etapa.descripcion}
                  </p>
                  {etapa.detalle && (
                    <div className="mt-1">
                      <span className="badge bg-light text-dark fs-10">
                        {etapa.detalle}
                      </span>
                    </div>
                  )}
                </div>

                {/* Tooltip */}
                <Tooltip 
                  placement="right" 
                  target={`etapa-${etapa.id}-${solicitud.identificador}`}
                  isOpen={tooltipOpen[`etapa-${etapa.id}-${solicitud.identificador}`]}
                  toggle={() => toggleTooltip(`etapa-${etapa.id}-${solicitud.identificador}`)}
                >
                  {etapa.tooltip}
                </Tooltip>
              </div>
            ))}
          </div>
        </CardBody>
      </Collapse>

      {/* Estilos inline para el stepper */}
      <style>{`
        .stepper-vertical {
          position: relative;
          padding-left: 0;
        }
        
        .stepper-item {
          position: relative;
          display: flex;
          align-items: flex-start;
          padding-bottom: 1.5rem;
          padding-left: 3rem;
        }
        
        .stepper-item:last-child {
          padding-bottom: 0;
        }
        
        .stepper-line {
          position: absolute;
          left: 15px;
          top: 32px;
          width: 2px;
          height: calc(100% - 16px);
          background-color: #e9ebec;
        }
        
        .stepper-line.completado {
          background-color: #0ab39c;
        }
        
        .stepper-icon {
          position: absolute;
          left: 0;
          top: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          z-index: 1;
        }
        
        .stepper-icon.completado {
          background-color: #0ab39c;
          color: white;
        }
        
        .stepper-icon.actual {
          background-color: #405189;
          color: white;
          box-shadow: 0 0 0 4px rgba(64, 81, 137, 0.2);
        }
        
        .stepper-icon.pendiente {
          background-color: #e9ebec;
          color: #878a99;
        }
        
        .stepper-content {
          flex: 1;
          min-width: 0;
        }
      `}</style>
    </Card>
  );
};

/**
 * Obtiene información del ciclo actual para solicitudes crónicas
 * 
 * Lógica de negocio:
 * - Rango válido de entregas: fechaCreacion a fechaVigencia
 * - La primera factura marca el día del mes para los ciclos siguientes
 * - Cada ciclo = mismo día del mes siguiente (aprox 30 días)
 * - Total ciclos = cuántos ciclos caben desde primera factura hasta fechaVigencia
 * - Ciclo actual = cantidad de facturas realizadas
 * - Máximo 6 ciclos posibles
 */
const obtenerInfoCiclo = (solicitud) => {
  const cronico = solicitud.cronico;
  if (!cronico) return null;

  const facturas = solicitud.facturacion || [];
  const numFacturas = facturas.length;
  const fechaVigencia = cronico.fechaVigencia ? new Date(cronico.fechaVigencia) : null;
  
  // Obtener fecha de la primera factura (marca el inicio de los ciclos)
  let fechaPrimeraFactura = null;
  if (numFacturas > 0 && facturas[0]?.fecha) {
    fechaPrimeraFactura = new Date(facturas[0].fecha);
  }

  // Calcular total de ciclos posibles
  let totalCiclos = 6; // Máximo por defecto
  
  if (fechaPrimeraFactura && fechaVigencia) {
    // Iterar desde la primera factura, avanzando mes a mes
    // hasta que la fecha del ciclo supere la fechaVigencia o lleguemos a 6
    let ciclosPosibles = 0;
    let fechaCiclo = new Date(fechaPrimeraFactura);
    
    while (fechaCiclo <= fechaVigencia && ciclosPosibles < 6) {
      ciclosPosibles++;
      // Avanzar al siguiente mes (mismo día)
      fechaCiclo.setMonth(fechaCiclo.getMonth() + 1);
    }
    
    totalCiclos = Math.max(ciclosPosibles, 1);
  } else if (cronico.fechaCreacion && fechaVigencia) {
    // Sin facturas: estimar desde fechaCreacion
    let ciclosPosibles = 0;
    let fechaCiclo = new Date(cronico.fechaCreacion);
    
    while (fechaCiclo <= fechaVigencia && ciclosPosibles < 6) {
      ciclosPosibles++;
      fechaCiclo.setMonth(fechaCiclo.getMonth() + 1);
    }
    
    totalCiclos = Math.max(ciclosPosibles, 1);
  }

  // Ciclo actual = cantidad de facturas realizadas
  let cicloActual = numFacturas > 0 ? Math.min(numFacturas, totalCiclos) : 1;

  return {
    cicloActual,
    totalCiclos,
    mesActual: cicloActual
  };
};

/**
 * Calcula las etapas para solicitudes CRÓNICAS (ciclo actual)
 * Flujo: Documentos → Medicamentos → Facturación → Dirección → Entrega
 */
const calcularEtapasCronicas = (solicitud) => {
  // Si la solicitud está "Entregada" o "Entregado", todo está completado
  const estaEntregada = solicitud.estatus === "Entregada" || solicitud.estatus === "Entregado";
  
  // 1. Validación de documentos
  const tieneDocumentos = !!(solicitud.recipe || solicitud.informe);
  const documentosVerificados = solicitud.verificacionDocumentos?.verificado === true;
  
  // 2. Medicamentos crónicos por ciclo
  // Verificar si hay medicamentos registrados en el historial de entregas del ciclo actual
  const historialEntregas = solicitud.cronico?.historialEntregas || [];
  const tieneMedicamentos = historialEntregas.length > 0;
  
  // 3. Facturación por ciclo
  const facturasDelMes = obtenerFacturasDelMesActual(solicitud);
  const tieneFacturaMes = estaEntregada || facturasDelMes.length > 0;
  
  // 4. Verificación de dirección
  const tieneCoordenadas = solicitud.coordenadas && solicitud.coordenadas.length === 2;
  const direccionVerificada = solicitud.verificacionDireccion?.verificado === true;
  
  // 5. Entrega por ciclo
  const deliveryDelMes = obtenerDeliveryDelMesActual(solicitud);
  const tieneDeliveryMes = deliveryDelMes !== null;
  const deliveryAsignado = tieneDeliveryMes && deliveryDelMes.responsable;
  const entregadoMes = estaEntregada || (tieneDeliveryMes && deliveryDelMes.estatus === "Entregado");
  const enTransito = solicitud.estatus === "En Tránsito" || solicitud.estatus === "En Transito";

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return null;
    try {
      return new Date(fecha).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return null;
    }
  };

  // Etapas para crónicas
  const etapas = [
    {
      id: "documentos",
      titulo: "Documentos Médicos",
      descripcion: documentosVerificados
        ? `Verificado por: ${solicitud.verificacionDocumentos?.farmaceutico || 'Farmacéutico'}`
        : tieneDocumentos
          ? "Pendiente verificación de documentos"
          : "Sin documentos adjuntos",
      icono: "ri-file-list-3-line",
      estado: documentosVerificados ? "completado" : (tieneDocumentos ? "actual" : "pendiente"),
      tooltip: "Verificación de recipe e informe médico",
      detalle: null
    },
    {
      id: "medicamentos",
      titulo: "Medicamentos del Ciclo",
      descripcion: tieneMedicamentos
        ? `${historialEntregas.length} entrega(s) registrada(s)`
        : !documentosVerificados
          ? "Requiere verificación de documentos"
          : "Pendiente registro de medicamentos",
      icono: "ri-medicine-bottle-line",
      estado: tieneMedicamentos ? "completado" : (documentosVerificados ? "actual" : "pendiente"),
      tooltip: "Registro de medicamentos crónicos para el ciclo",
      detalle: null
    },
    {
      id: "facturacion",
      titulo: "Facturación del Ciclo",
      descripcion: tieneFacturaMes
        ? `Factura del ciclo registrada`
        : !tieneMedicamentos
          ? "Requiere medicamentos registrados"
          : "Pendiente factura del ciclo",
      icono: "ri-bill-line",
      estado: tieneFacturaMes ? "completado" : (tieneMedicamentos ? "actual" : "pendiente"),
      tooltip: "Registro de factura del ciclo mensual",
      fecha: tieneFacturaMes && facturasDelMes[0]?.fecha ? formatFecha(facturasDelMes[0].fecha) : null,
      detalle: null
    },
    {
      id: "direccion",
      titulo: "Validación Dirección",
      descripcion: direccionVerificada
        ? `Verificado por: ${solicitud.verificacionDireccion?.verificadoPor || 'Operador'}`
        : tieneCoordenadas
          ? "Pendiente confirmación de dirección"
          : "Pendiente validación de coordenadas",
      icono: "ri-map-pin-line",
      estado: direccionVerificada ? "completado" : (tieneFacturaMes ? "actual" : "pendiente"),
      tooltip: "Validación de dirección y coordenadas de entrega",
      detalle: null
    },
    {
      id: "entrega",
      titulo: "Entrega del Ciclo",
      descripcion: entregadoMes
        ? "Entrega del ciclo completada"
        : (enTransito || deliveryAsignado)
          ? `Asignado a: ${deliveryDelMes?.responsable || 'Motorizado'}`
          : !direccionVerificada
            ? "Requiere verificación de dirección"
            : "Pendiente asignación de delivery",
      icono: "ri-truck-line",
      estado: entregadoMes ? "completado" : ((enTransito || deliveryAsignado) ? "actual" : (direccionVerificada ? "actual" : "pendiente")),
      tooltip: "Entrega de medicamentos del ciclo",
      fecha: entregadoMes && deliveryDelMes?.fecha ? formatFecha(deliveryDelMes.fecha) : null,
      detalle: null
    }
  ];

  return etapas;
};

/**
 * Calcula las etapas para solicitudes AGUDAS
 */
const calcularEtapasAgudas = (solicitud) => {
  // Verificar condiciones
  const tieneDocumentos = !!(solicitud.recipe || solicitud.informe);
  const documentosVerificados = solicitud.verificacionDocumentos?.verificado === true;
  // Diagnóstico se considera completado SOLO si tiene codigoDiagnosticoFarmacologico (no null)
  const tieneDiagnostico = !!solicitud.codigoDiagnosticoFarmacologico;
  const tieneFactura = solicitud.facturacion && solicitud.facturacion.length > 0;
  const tieneCoordenadas = solicitud.coordenadas && solicitud.coordenadas.length === 2;
  const direccionVerificada = solicitud.verificacionDireccion?.verificado === true;
  const tieneDelivery = solicitud.delivery && solicitud.delivery.length > 0;
  const ultimoDelivery = tieneDelivery ? solicitud.delivery[solicitud.delivery.length - 1] : null;
  const deliveryAsignado = ultimoDelivery?.responsable;
  const entregado = solicitud.estatus === "Entregado";
  // Si está "En Tránsito" o "En Transito", ya tiene delivery asignado
  const enTransito = solicitud.estatus === "En Tránsito" || solicitud.estatus === "En Transito";

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return null;
    try {
      return new Date(fecha).toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return null;
    }
  };

  // Etapas para agudas
  const etapas = [
    {
      id: "documentos",
      titulo: "Documentos Médicos",
      descripcion: documentosVerificados
        ? `Verificado por: ${solicitud.verificacionDocumentos?.farmaceutico || 'Farmacéutico'}`
        : tieneDocumentos 
          ? "Pendiente verificación del farmacéutico" 
          : "Pendiente carga de recipe e informe",
      icono: "ri-file-text-line",
      estado: documentosVerificados ? "completado" : (tieneDocumentos ? "actual" : "pendiente"),
      tooltip: "Verificación de recipe e informe médico por el farmacéutico",
      fecha: documentosVerificados ? formatFecha(solicitud.verificacionDocumentos?.fecha) : null,
      detalle: null
    },
    {
      id: "diagnostico",
      titulo: "Diagnóstico & Cobertura",
      descripcion: tieneDiagnostico
        ? `Diagnóstico: ${solicitud.diagnosticoFarmacologico?.substring(0, 30) || 'Seleccionado'}...`
        : !documentosVerificados
          ? "Requiere verificación de documentos"
          : "Pendiente selección de diagnóstico",
      icono: "ri-stethoscope-line",
      estado: tieneDiagnostico ? "completado" : (documentosVerificados ? "actual" : "pendiente"),
      tooltip: "Selección de diagnóstico y consulta de cobertura",
      detalle: null
    },
    {
      id: "facturacion",
      titulo: "Facturación",
      descripcion: tieneFactura 
        ? `${solicitud.facturacion.length} factura(s) registrada(s)`
        : !tieneDiagnostico
          ? "Requiere diagnóstico para facturar"
          : "Pendiente registro de factura",
      icono: "ri-bill-line",
      estado: tieneFactura ? "completado" : (tieneDiagnostico ? "actual" : "pendiente"),
      tooltip: "Registro de factura de la farmacia (requiere diagnóstico previo)",
      fecha: tieneFactura ? formatFecha(solicitud.facturacion[0]?.fecha) : null,
      detalle: null
    },
    {
      id: "direccion",
      titulo: "Validación Dirección",
      descripcion: direccionVerificada
        ? `Verificado por: ${solicitud.verificacionDireccion?.verificadoPor || 'Operador'}`
        : tieneCoordenadas 
          ? "Pendiente confirmación de dirección"
          : "Pendiente validación de coordenadas",
      icono: "ri-map-pin-line",
      estado: direccionVerificada ? "completado" : (tieneFactura ? "actual" : "pendiente"),
      tooltip: "Validación de dirección y coordenadas de entrega",
      detalle: null
    },
    {
      id: "entrega",
      titulo: "Entrega",
      descripcion: entregado 
        ? "Solicitud entregada exitosamente"
        : (enTransito || deliveryAsignado)
          ? `Asignado a: ${ultimoDelivery?.responsable || 'Motorizado'}`
          : !direccionVerificada
            ? "Requiere verificación de dirección"
            : "Pendiente asignación de delivery",
      icono: "ri-truck-line",
      estado: entregado ? "completado" : ((enTransito || deliveryAsignado) ? "actual" : (direccionVerificada ? "actual" : "pendiente")),
      tooltip: "Asignación de delivery y entrega final",
      fecha: entregado && ultimoDelivery?.fecha ? formatFecha(ultimoDelivery.fecha) : null,
      detalle: null
    }
  ];

  return etapas;
};

/**
 * Obtiene las facturas del mes actual para crónicas
 */
const obtenerFacturasDelMesActual = (solicitud) => {
  if (!solicitud.facturacion || solicitud.facturacion.length === 0) return [];
  
  const ahora = new Date();
  const mesActual = ahora.getMonth();
  const anioActual = ahora.getFullYear();
  
  return solicitud.facturacion.filter(factura => {
    if (!factura.fecha) return false;
    const fechaFactura = new Date(factura.fecha);
    return fechaFactura.getMonth() === mesActual && fechaFactura.getFullYear() === anioActual;
  });
};

/**
 * Obtiene el delivery del mes actual para crónicas
 */
const obtenerDeliveryDelMesActual = (solicitud) => {
  if (!solicitud.delivery || solicitud.delivery.length === 0) return null;
  
  const ahora = new Date();
  const mesActual = ahora.getMonth();
  const anioActual = ahora.getFullYear();
  
  // Buscar delivery del mes actual
  const deliveryMes = solicitud.delivery.find(d => {
    if (!d.fecha) return false;
    const fechaDelivery = new Date(d.fecha);
    return fechaDelivery.getMonth() === mesActual && fechaDelivery.getFullYear() === anioActual;
  });
  
  // Si no hay del mes actual, retornar el último
  return deliveryMes || solicitud.delivery[solicitud.delivery.length - 1];
};

/**
 * Calcula el porcentaje de avance basado en las etapas completadas
 */
const calcularPorcentaje = (etapas) => {
  const totalEtapas = etapas.length;
  let completadas = 0;
  let enProgreso = 0;

  etapas.forEach(etapa => {
    if (etapa.estado === 'completado') completadas++;
    if (etapa.estado === 'actual') enProgreso = 0.5; // Media etapa
  });

  const porcentaje = ((completadas + enProgreso) / totalEtapas) * 100;
  return Math.min(porcentaje, 100);
};

export default ProgresoSolicitud;
